import React, { useEffect, useMemo, useState } from "react"
import { Container } from "reactstrap"
import { Link, useLocation, useNavigate } from "react-router-dom"
import ListingTabs from "./ListingTabs"
import { isPermitted, firstPermittedIndex } from "../Functions/permissions"
import { FILTER_PARAM } from "../Filters/filterUrl"
// Reuses the codebase's one "insufficient permissions" look (see Components/RestrictedSection.js)
// rather than importing that component directly: RestrictedSection is a HOC wired to its own
// single-permission network-only query, which is the right shape for gating one panel but
// doesn't fit here - a merged page can have several tabs, each with its own permission, and
// this message covers "none of them", not one named requiredPermission. Matching its copy and
// classes (via the same stylesheet) keeps the two visually identical without forcing a second,
// redundant permissions fetch just to render text.
import "../Components/RestrictedSection.css"
import "./TabbedListingPage.css"

// Writes the new tab hash through navigate(), so react-router's own location (read by
// useLocation() everywhere else in the tree, e.g. Filters/filterUrl.js and this component
// itself) is the single source of truth for which tab is active. There is deliberately no
// companion `window.location.hash = …` write here any more: react-router 6.30 already moves
// the address bar via pushState/replaceState, so that second write was a no-op under a real
// BrowserRouter, and under MemoryRouter (what every test here uses) it did nothing at all -
// MemoryRouter never touches `window.location`. Keying tab resolution off useLocation()
// instead (below) makes that write unnecessary either way, and also fixes Back/Forward: per
// the HTML spec, `hashchange` only fires when two adjacent history entries are identical
// apart from the fragment, and switching tabs always changes `?filter=` too (see
// `clearFilters` below), so Back from one tab to another was landing on `popstate` with no
// matching `hashchange` and this shell would silently miss it. react-router updates its own
// location on `popstate` unconditionally, so reading `location.hash` from useLocation()
// instead of `window.location.hash` means Back/Forward just works, without a second listener.
// `clearFilters` drops the shared `?filter=` advanced-filter param: it is one page-scoped
// param (Filters/filterUrl.js), but several sibling panels now share a single page, so a
// filter applied on one tab must not bleed into the next tab's GraphQL `filters` variable.
function writeTabHash(location, navigate, hash, { replace = false, clearFilters = false } = {}) {
    const params = new URLSearchParams(location.search)
    if (clearFilters) params.delete(FILTER_PARAM)
    navigate({ pathname: location.pathname, search: params.toString(), hash }, { replace })
}

// One shell for every tabbed listing page: renders the tab strip, keeps the active
// tab in the location hash, locks tabs the user lacks permission for, and lands on
// the first tab the user can actually open.
const TabbedListingPage = ({ tabs = [], hasPermissions, loading = false, className = "", onTabChange }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const [activeTabId, setActiveTabId] = useState(null)

    const hasGatedTabs = useMemo(() => tabs.some(tab => !!tab.permissionRequired), [tabs])
    // Only a gated page needs to wait: an ungated page can pick its tab straight away.
    const waitingOnPermissions = hasGatedTabs && loading

    // Keyed on location.hash (react-router's own, via useLocation() above) rather than
    // window.location.hash, so this also re-runs on browser Back/Forward: react-router
    // updates its location on every `popstate`, regardless of whether the search string
    // changed too, which is exactly the case a tab switch produces (see writeTabHash's
    // comment above) and which `hashchange` alone would miss.
    useEffect(() => {
        if (waitingOnPermissions || tabs.length === 0) return

        const hash = location.hash
        const hashed = tabs.find(tab => tab.id === hash)

        const target = hashed && isPermitted(hashed, hasPermissions)
            ? hashed
            : tabs[firstPermittedIndex(tabs, hasPermissions)]

        if (!target) return
        // A real switch away from a tab this shell had already settled on (not the very
        // first resolution on mount, and not just re-affirming the same tab after e.g. a
        // permissions refetch) -- the outgoing tab's advanced filters don't apply here.
        const isTabSwitch = activeTabId !== null && target.id !== activeTabId
        if (location.hash !== target.id) writeTabHash(location, navigate, target.id, { replace: true, clearFilters: isTabSwitch })
        if (target.id !== activeTabId) {
            setActiveTabId(target.id)
            onTabChange?.(target)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waitingOnPermissions, hasPermissions, location.hash])

    const changeTab = (tabId) => {
        const tab = tabs.find(item => item.id === tabId)
        if (!tab || !isPermitted(tab, hasPermissions) || tab.id === activeTabId) return
        writeTabHash(location, navigate, tab.id, { clearFilters: true })
        setActiveTabId(tab.id)
        onTabChange?.(tab)
    }

    if (waitingOnPermissions) return <Container fluid className={className} />

    const decoratedTabs = tabs.map(tab => ({ ...tab, disabled: !isPermitted(tab, hasPermissions) }))
    const activeTab = tabs.find(tab => tab.id === activeTabId)
    // A merged page whose every tab is gated, viewed by a user who holds none of those
    // permissions, used to fall through to an empty body below the tab strip - previously
    // that same user would never have reached the page at all (the whole sidebar group was
    // hidden). Now the nav item is reachable but locked-looking; show the standard
    // insufficient-permissions message instead of a page that looks broken.
    const noTabPermitted = tabs.length > 0 && !tabs.some(tab => isPermitted(tab, hasPermissions))

    return (
        <Container fluid className={`bos-tabbed-listing-page ${className}`.trim()}>
            <ListingTabs tabs={decoratedTabs} activeTab={activeTabId} onChange={changeTab} />
            {noTabPermitted ?
                <div className="permission-box">
                    <div className="permission-box-title">
                        <h4> Insufficient Permissions&nbsp;&nbsp;<i className="fa fa-meh-o" aria-hidden="true"></i></h4>
                    </div>
                    <div className="permission-box-body">
                        <p className="permission-box-message">You don't have enough permissions to access this page. Please contact the admin for further assistance.</p>
                    </div>
                    <br /><br />
                    <div className="permission-box-footer">
                        <Link className="permission-box-button btn btn-primary" to="/">
                            Go To Homepage
                        </Link>
                    </div>
                </div> :
                activeTab && isPermitted(activeTab, hasPermissions) && activeTab.render()
            }
        </Container>
    )
}

export default TabbedListingPage
